/**
 * useSEO – Professional per-page SEO hook for TK Fashion & Design
 *
 * Manages:
 *  • document.title
 *  • <meta name="description">
 *  • <meta name="robots">
 *  • <link rel="canonical">
 *  • Open Graph tags (og:*)
 *  • Twitter Card tags
 *  • JSON-LD structured data (page-specific)
 *  • JSON-LD breadcrumb list
 *
 * No external dependency – pure DOM manipulation in a useEffect.
 */

import { useEffect } from 'react';

export const SITE_NAME   = 'TK Fashion & Design';
export const BASE_URL    = 'https://tkfashiondesign.com';
export const LOGO_URL    = `${BASE_URL}/TKfashion_logo.jpg`;

export const DEFAULT_DESCRIPTION =
  'Shop premium fashion and quality clothing at TK Fashion & Design. ' +
  'Browse our latest styles, choose your sizes and colours, and shop with confidence – ' +
  'delivered across Ghana.';

// ─── DOM helpers ──────────────────────────────────────────────────────────────

function setMeta(attrName, attrValue, content) {
  let el = document.querySelector(`meta[${attrName}="${attrValue}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data, null, 0);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * @param {object} params
 * @param {string}  [params.title]        – Page title (site name appended automatically)
 * @param {string}  [params.description]  – Meta description (155–160 chars ideal)
 * @param {string}  [params.canonical]    – Absolute or root-relative canonical path, e.g. "/cart"
 * @param {string}  [params.ogImage]      – Absolute URL for og:image
 * @param {string}  [params.ogType]       – "website" | "product" | "article"
 * @param {boolean} [params.noIndex]      – true → noindex,nofollow
 * @param {object|object[]} [params.jsonLd] – Schema.org JSON-LD object(s)
 * @param {{ name: string, path: string }[]} [params.breadcrumbs] – Breadcrumb items
 */
export function useSEO({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogImage = LOGO_URL,
  ogType = 'website',
  noIndex = false,
  jsonLd = null,
  breadcrumbs = null,
} = {}) {
  const fullTitle = title
    ? `${title} – ${SITE_NAME}`
    : `${SITE_NAME} – Premium Fashion in Ghana`;

  const canonicalUrl = canonical
    ? (canonical.startsWith('http') ? canonical : `${BASE_URL}${canonical}`)
    : `${BASE_URL}${window.location.pathname}`;

  const robotsContent = noIndex
    ? 'noindex,nofollow'
    : 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1';

  useEffect(() => {
    // ── Title
    document.title = fullTitle;

    // ── Core meta
    setMeta('name', 'description', description);
    setMeta('name', 'robots', robotsContent);

    // ── Canonical
    setLink('canonical', canonicalUrl);

    // ── Open Graph
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:locale',    'en_GH');
    setMeta('property', 'og:type',      ogType);
    setMeta('property', 'og:title',     fullTitle);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url',       canonicalUrl);
    setMeta('property', 'og:image',     ogImage);
    setMeta('property', 'og:image:alt', title || SITE_NAME);

    // ── Twitter Card
    setMeta('name', 'twitter:card',        'summary_large_image');
    setMeta('name', 'twitter:title',       fullTitle);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image',       ogImage);
    setMeta('name', 'twitter:image:alt',   title || SITE_NAME);

    // ── Page-level JSON-LD
    if (jsonLd) {
      const schema = Array.isArray(jsonLd)
        ? { '@context': 'https://schema.org', '@graph': jsonLd }
        : jsonLd;
      upsertJsonLd('tk-page-ld', schema);
    } else {
      removeJsonLd('tk-page-ld');
    }

    // ── Breadcrumb JSON-LD
    if (breadcrumbs && breadcrumbs.length > 0) {
      upsertJsonLd('tk-breadcrumb-ld', {
        '@context': 'https://schema.org',
        '@type':    'BreadcrumbList',
        itemListElement: breadcrumbs.map((item, index) => ({
          '@type':    'ListItem',
          position:   index + 1,
          name:       item.name,
          item:       `${BASE_URL}${item.path}`,
        })),
      });
    } else {
      removeJsonLd('tk-breadcrumb-ld');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullTitle, description, canonicalUrl, ogImage, ogType, robotsContent]);
}

// ─── Pre-built page configs ────────────────────────────────────────────────────

export const PAGE_SEO = {
  home: {
    title:       'Shop Premium Fashion',
    description: 'Browse TK Fashion & Design\'s latest collection. Premium clothing, trendy styles, and fast delivery across Ghana. Shop tops, bottoms, accessories and more.',
    canonical:   '/',
    breadcrumbs: [{ name: 'Shop', path: '/' }],
  },
  login: {
    title:       'Sign In',
    description: 'Sign in to your TK Fashion & Design account to track orders, manage your cart, and access exclusive deals.',
    canonical:   '/login',
    noIndex:     true,
  },
  register: {
    title:       'Create Account',
    description: 'Create a free TK Fashion & Design account. Get started shopping premium fashion delivered across Ghana.',
    canonical:   '/register',
    noIndex:     true,
  },
  forgotPassword: {
    title:    'Forgot Password',
    canonical: '/forgot-password',
    noIndex:   true,
  },
  resetPassword: {
    title:    'Reset Password',
    canonical: '/reset-password',
    noIndex:   true,
  },
  cart: {
    title:    'Your Cart',
    canonical: '/cart',
    noIndex:   true,
  },
  checkout: {
    title:    'Checkout',
    canonical: '/checkout',
    noIndex:   true,
  },
  payment: {
    title:    'Payment',
    canonical: '/payment',
    noIndex:   true,
  },
  settings: {
    title:    'Account Settings',
    canonical: '/settings',
    noIndex:   true,
  },
  orders: {
    title:    'My Orders',
    canonical: '/orders',
    noIndex:   true,
  },
  orderDetails: {
    title:    'Order Details',
    noIndex:   true,
  },
  support: {
    title:    'Customer Support',
    description: 'Get help with your TK Fashion & Design orders. Chat with our support team for fast assistance.',
    canonical: '/support',
    noIndex:   true,
  },
  notifications: {
    title:    'Notifications',
    canonical: '/notifications',
    noIndex:   true,
  },
  acceptancePolicy: {
    title:       'Acceptance Policy',
    description: 'Read TK Fashion & Design\'s acceptance and return policy. Understand how we handle product acceptance, returns, and exchanges.',
    canonical:   '/policies/acceptance',
    breadcrumbs: [{ name: 'Shop', path: '/' }, { name: 'Acceptance Policy', path: '/policies/acceptance' }],
  },
  termsPolicy: {
    title:       'Terms & Conditions',
    description: 'Read the terms and conditions of TK Fashion & Design. Understand your rights and obligations when shopping with us.',
    canonical:   '/policies/terms',
    breadcrumbs: [{ name: 'Shop', path: '/' }, { name: 'Terms & Conditions', path: '/policies/terms' }],
  },
  privacyPolicy: {
    title:       'Privacy Policy',
    description: 'Read how TK Fashion & Design collects, uses, and protects your personal data. Your privacy is our priority.',
    canonical:   '/policies/privacy',
    breadcrumbs: [{ name: 'Shop', path: '/' }, { name: 'Privacy Policy', path: '/policies/privacy' }],
  },
};

// ─── Product page SEO builder ──────────────────────────────────────────────────

/**
 * Builds a complete SEO config for a single product page.
 * @param {object} product  – Product document from the API
 * @param {string} baseUrl  – Frontend base URL
 * @returns {object}        – Props to spread into useSEO()
 */
export function buildProductSEO(product, baseUrl = BASE_URL) {
  if (!product) return {};

  const name        = product.name || 'Product';
  const category    = product.category || 'Fashion';
  const description = product.description
    ? `${product.description.slice(0, 140)}…`
    : `Shop ${name} at TK Fashion & Design. Premium ${category} style available in multiple sizes and colours, delivered across Ghana.`;

  const firstImage  = (product.images || [])[0];
  const ogImage     = firstImage
    ? (firstImage.startsWith('http') ? firstImage : `${baseUrl}${firstImage}`)
    : LOGO_URL;

  // Price – try to surface a number for schema
  let offerPrice = null;
  if (product.pricing?.mode === 'flat' && product.pricing?.flatPrice != null) {
    offerPrice = Number(product.pricing.flatPrice);
  } else if (product.pricing?.mode === 'per-size') {
    const values = Object.values(product.pricing?.sizePrices || {}).map(Number).filter(Boolean);
    if (values.length > 0) offerPrice = Math.min(...values);
  } else if (product.pricing?.mode === 'per-color') {
    const values = Object.values(product.pricing?.colorPrices || {}).map(Number).filter(Boolean);
    if (values.length > 0) offerPrice = Math.min(...values);
  }

  const canonical = `/product/${product._id}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type':    'Product',
    name,
    description,
    image:      ogImage,
    brand: {
      '@type': 'Brand',
      name:    SITE_NAME,
    },
    category,
    url: `${baseUrl}${canonical}`,
    ...(offerPrice != null && {
      offers: {
        '@type':         'Offer',
        price:           offerPrice.toFixed(2),
        priceCurrency:   'GHS',
        availability:    'https://schema.org/InStock',
        url:             `${baseUrl}${canonical}`,
        seller: {
          '@type': 'Organization',
          name:    SITE_NAME,
        },
      },
    }),
  };

  return {
    title:       name,
    description,
    canonical,
    ogImage,
    ogType:      'product',
    jsonLd,
    breadcrumbs: [
      { name: 'Shop',  path: '/' },
      { name,          path: canonical },
    ],
  };
}
